import { useCallback, useEffect, useMemo, useState } from "react";
import Header from "../component/Header";
import Footer from "../component/Footer";
import LanguagePopup from "../component/LanguagePopup";
import BeneficiarySidebarProfile from "../component/BeneficiarySidebarProfile";
import { Link } from "../component/AppLink";
import { ArabicFooter, ArabicHeader } from "./ArabicHome";
import { getApiUrl } from "../api";
import { getStoredToken, redirectToLogin } from "../auth";
import "./InKindAssistance.css";

const labels = {
  en: {
    title: "In-Kind Assistance", intro: "Request available household items and review your assistance records.",
    details: "Assistance Details", detailsHint: "Choose a category, enter the required quantities, and explain your request.",
    category: "Assistance Category", select: "Select assistance category", items: "Items and Quantities",
    reason: "Reason for Request *", reasonPlaceholder: "Describe why this assistance is needed",
    notes: "Additional Notes", notesPlaceholder: "Add any information that may help us review the request",
    method: "Fulfillment Method", home: "Home Delivery", collection: "Collection from Tarahum",
    check: "Check Eligibility", submit: "Submit Request", submitting: "Submitting...", eligible: "Eligible", notEligible: "Not Eligible",
    history: "Assistance History", requests: "My Requests", noRequests: "No requests yet.", noHistory: "No completed in-kind assistance yet.",
    available: "available", maximum: "Maximum", waiting: "Waiting period", months: "month(s)", nextEligibility: "Next eligibility",
    loading: "Loading in-kind assistance...", menu: "Beneficiary Menu", closeMenu: "Close Menu",
    emiratesIdDocument: "Emirates ID Copy *", emiratesIdHint: "Upload a clear PDF, JPG, or PNG copy (maximum 10 MB).",
    emiratesIdRequired: "Upload your Emirates ID before submitting the request.", invalidDocument: "Upload a PDF, JPG, or PNG file up to 10 MB.",
  },
  ar: {
    title: "المساعدة العينية", intro: "اطلب المواد المنزلية المتاحة وراجع سجلات المساعدة الخاصة بك.",
    details: "تفاصيل المساعدة", detailsHint: "اختر الفئة وأدخل الكميات المطلوبة واشرح سبب الطلب.",
    category: "فئة المساعدة", select: "اختر فئة المساعدة", items: "المواد والكميات",
    reason: "سبب الطلب *", reasonPlaceholder: "اشرح سبب الحاجة إلى هذه المساعدة",
    notes: "ملاحظات إضافية", notesPlaceholder: "أضف أي معلومات تساعدنا في مراجعة الطلب",
    method: "طريقة الاستلام", home: "التوصيل للمنزل", collection: "الاستلام من تراحم",
    check: "تحقق من الأهلية", submit: "إرسال الطلب", submitting: "جارٍ الإرسال...", eligible: "مؤهل", notEligible: "غير مؤهل",
    history: "سجل المساعدات", requests: "طلباتي", noRequests: "لا توجد طلبات بعد.", noHistory: "لا توجد مساعدات عينية مكتملة بعد.",
    available: "متاح", maximum: "الحد الأقصى", waiting: "فترة الانتظار", months: "شهر", nextEligibility: "الأهلية القادمة",
    loading: "جارٍ تحميل المساعدة العينية...", menu: "قائمة المستفيد", closeMenu: "إغلاق القائمة",
    emiratesIdDocument: "صورة الهوية الإماراتية *", emiratesIdHint: "ارفع نسخة واضحة بصيغة PDF أو JPG أو PNG (بحد أقصى 10 ميجابايت).",
    emiratesIdRequired: "يرجى رفع الهوية الإماراتية قبل إرسال الطلب.", invalidDocument: "ارفع ملف PDF أو JPG أو PNG بحجم لا يتجاوز 10 ميجابايت.",
  },
};

const menuItems = [
  ["dashboard-tab", "fa-solid fa-house", "Dashboard", "لوحة التحكم"],
  ["family-tab", "fa-solid fa-users", "Family Details", "بيانات الأسرة"],
  ["profile-tab", "fa-regular fa-user", "Profile", "الملف الشخصي"],
  ["financial-tab", "fa-solid fa-wallet", "Financial Info", "المعلومات المالية"],
  ["Aid-Requests", "fa-solid fa-hand-holding-heart", "Aid Requests", "طلبات المساعدة"],
];

const menuItemsAfterCurrent = [
  ["appointment-tab", "fa-regular fa-calendar", "Appointments", "المواعيد"],
  ["Settings-tab", "fa-solid fa-sliders", "Settings", "الإعدادات"],
];

const parseApiDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const text = String(value).trim();
  const aspNetDate = /^\/Date\((-?\d+)(?:[+-]\d{4})?\)\/$/.exec(text);
  const date = aspNetDate
    ? new Date(Number(aspNetDate[1]))
    : new Date(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(text) ? text.replace(" ", "T") : text);

  return Number.isNaN(date.getTime()) ? null : date;
};

export default function InKindAssistance({ language = "en" }) {
  const isArabic = language === "ar";
  const t = labels[language] || labels.en;
  const token = getStoredToken();
  const [data, setData] = useState(null);
  const [familyFileStatus, setFamilyFileStatus] = useState(null);
  const [categoryId, setCategoryId] = useState("");
  const [quantities, setQuantities] = useState({});
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [method, setMethod] = useState("HOME_DELIVERY");
  const [eligibility, setEligibility] = useState(null);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [emiratesIdDocument, setEmiratesIdDocument] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);

  const requestHeaders = useMemo(() => ({ Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) }), [token]);
  const formatDate = useCallback((value) => {
    const date = parseApiDate(value);
    return date
      ? new Intl.DateTimeFormat(isArabic ? "ar-AE" : "en-AE", { dateStyle: "medium" }).format(date)
      : "—";
  }, [isArabic]);

  const load = useCallback(async () => {
    const [response, familyFileResponse] = await Promise.all([
      fetch(getApiUrl("/api/InKindAssistancePortal.ashx"), { credentials: "include", headers: requestHeaders }),
      fetch(getApiUrl("/api/UnifiedFamilyFile.ashx"), { credentials: "include", headers: requestHeaders }),
    ]);
    if (response.status === 401 || familyFileResponse.status === 401) { redirectToLogin(); return; }
    const [result, familyFileResult] = await Promise.all([
      response.json().catch(() => ({})),
      familyFileResponse.json().catch(() => ({})),
    ]);
    if (!response.ok || !result.success) throw new Error(result.message || "Unable to load assistance.");
    if (!familyFileResponse.ok || !familyFileResult.success) throw new Error(familyFileResult.message || "Unable to load household eligibility.");
    setData(result);
    setFamilyFileStatus(familyFileResult);
  }, [requestHeaders]);

  useEffect(() => {
    document.title = `${t.title} | Tarahum Charity Foundation`;
    load().catch((error) => setMessage({ error: true, text: error.message }));
  }, [load, t.title]);

  useEffect(() => {
    if (!isArabic) return undefined;

    document.documentElement.lang = "ar";
    document.documentElement.dir = "rtl";
    document.body.classList.add("arabic-layout-page");

    return () => {
      document.documentElement.lang = "en";
      document.documentElement.dir = "ltr";
      document.body.classList.remove("arabic-layout-page");
    };
  }, [isArabic]);

  const category = useMemo(() => data?.categories?.find((item) => String(item.id) === String(categoryId)), [data, categoryId]);
  const totalQuantity = Object.values(quantities).reduce((sum, value) => sum + Number(value || 0), 0);
  const canApply = familyFileStatus?.eligible === true;

  const checkEligibility = () => {
    if (!canApply) { setEligibility({ eligible: false, reason: familyFileStatus?.message || (isArabic ? "يجب التحقق من ملف الأسرة أولاً." : "A verified household is required.") }); return; }
    if (!category) { setEligibility({ eligible: false, reason: isArabic ? "اختر فئة المساعدة." : "Select a category." }); return; }
    const openRequest = data.requests.find((request) => String(request.category_name) === String(category.category_name) && !["COMPLETED", "REJECTED", "CANCELLED"].includes(request.status_code));
    const lastAssistance = data.history.find((record) => String(record.category_name) === String(category.category_name));
    const nextEligibleDate = parseApiDate(lastAssistance?.next_eligible_date);
    let result = { eligible: true, reason: t.eligible };
    if (data.profile?.file_status !== "ACTIVE") result = { eligible: false, reason: isArabic ? "ملف المستفيد غير نشط." : "Beneficiary file is not active." };
    else if (openRequest) result = { eligible: false, reason: isArabic ? "يوجد طلب مفتوح لهذه الفئة." : "An open request already exists for this category." };
    else if (nextEligibleDate && new Date() < nextEligibleDate) result = { eligible: false, reason: `${t.nextEligibility}: ${formatDate(nextEligibleDate)}` };
    else if (totalQuantity > Number(category.maximum_quantity)) result = { eligible: false, reason: `${t.maximum}: ${category.maximum_quantity}.` };
    else if (totalQuantity > Number(category.available_quantity)) result = { eligible: false, reason: `${t.available}: ${category.available_quantity}.` };
    else if (totalQuantity <= 0) result = { eligible: false, reason: isArabic ? "اختر كمية واحدة على الأقل." : "Select at least one item quantity." };
    setEligibility(result);
  };

  const selectEmiratesIdDocument = (event) => {
    const file = event.target.files?.[0] || null;
    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
    if (file && (!allowedTypes.includes(file.type) || file.size > 10 * 1024 * 1024)) {
      event.target.value = "";
      setEmiratesIdDocument(null);
      setMessage({ error: true, text: t.invalidDocument });
      return;
    }
    setMessage(null);
    setEmiratesIdDocument(file);
  };

  const submit = async () => {
    if (!emiratesIdDocument) {
      setMessage({ error: true, text: t.emiratesIdRequired });
      return;
    }
    setSaving(true); setMessage(null);
    try {
      const body = {
        action: "createRequest", categoryId,
        items: (category?.items || []).map((item) => ({ itemTypeId: item.id, quantity: quantities[item.id] || 0 })).filter((item) => Number(item.quantity) > 0),
        requestReason: reason, notes, fulfillmentMethod: method, address: data.profile?.address || "", area: data.profile?.area || "",
      };
      const formData = new FormData();
      formData.append("payload", JSON.stringify(body));
      formData.append("emiratesIdDocument", emiratesIdDocument, emiratesIdDocument.name);
      const response = await fetch(getApiUrl("/api/InKindAssistancePortal.ashx"), { method: "POST", credentials: "include", headers: requestHeaders, body: formData });
      if (response.status === 401) { redirectToLogin(); return; }
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) throw new Error(result.message || "Unable to submit request.");
      setMessage({ text: `${result.message} ${result.data.requestNumber}` });
      setCategoryId(""); setQuantities({}); setReason(""); setNotes(""); setEligibility(null); setEmiratesIdDocument(null); setFileInputKey((value) => value + 1);
      await load();
    } catch (error) { setMessage({ error: true, text: error.message }); }
    finally { setSaving(false); }
  };

  const dashboardHref = (tab) => `/beneficent-dashboard?tab=${encodeURIComponent(tab)}`;
  const closeMenu = () => setMenuOpen(false);

  return <>
    {isArabic ? <ArabicHeader /> : <><Header /><LanguagePopup /></>}
    <main className={`iap-dashboard-page${isArabic ? " arabic-beneficiary-dashboard" : ""}`} dir={isArabic ? "rtl" : "ltr"}>
      <section className="benifi-dash-bord-wrap"><div className="container"><div className={`benifi-dash-bord${menuOpen ? " beni-menu-open" : ""}`}>
        <button className="beni-mobile-menu-toggle" type="button" aria-expanded={menuOpen} aria-controls="inKindBeneficiarySidebar" onClick={() => setMenuOpen((open) => !open)}><i className={`fa-solid ${menuOpen ? "fa-xmark" : "fa-bars"}`}></i>{menuOpen ? t.closeMenu : t.menu}</button>
        <button className="beni-mobile-menu-backdrop" type="button" aria-label={t.closeMenu} onClick={closeMenu}></button>
        <aside className="beni-sidebar" id="inKindBeneficiarySidebar">
          <BeneficiarySidebarProfile language={language} />
          <nav className="beni-menu-wrap" aria-label={t.menu}>
            {menuItems.map(([tab, icon, english, arabic]) => <Link className="beni-menu" to={dashboardHref(tab)} key={tab} onClick={closeMenu}><i className={icon}></i>{isArabic ? arabic : english}</Link>)}
            <Link className="beni-menu active" to="/in-kind-assistance" onClick={closeMenu}><i className="fa-solid fa-box-open"></i>{t.title}</Link>
            {menuItemsAfterCurrent.map(([tab, icon, english, arabic]) => <Link className="beni-menu" to={dashboardHref(tab)} key={tab} onClick={closeMenu}><i className={icon}></i>{isArabic ? arabic : english}</Link>)}
          </nav>
        </aside>
        <div className="beni-main"><div className="beni-tab-content active"><div className="content-card iap-content-card">
          <div className="card-title iap-page-title"><h2>{t.title}</h2><p>{t.intro}</p></div>
          {message ? <div className={`iap-message${message.error ? " error" : ""}`} role="status">{message.text}</div> : null}
          {!data ? <div className="iap-loading"><i className="fa-solid fa-spinner fa-spin"></i>{t.loading}</div> : <>
            {familyFileStatus && !canApply ? <div className="iap-message error" role="status">
              {familyFileStatus?.message || (isArabic ? "أكمل التحقق من ملف الأسرة في قسم بيانات الأسرة أولاً." : "Complete Family File verification in the Family Details section first.")}
              {" "}<Link to={dashboardHref("family-tab")}>{isArabic ? "الانتقال إلى بيانات الأسرة" : "Go to Family Details"}</Link>
            </div> : null}
            {canApply ? <section className="iap-form-section" aria-labelledby="inKindAssistanceDetails">
              <div className="iap-section-heading"><i className="fa-solid fa-hand-holding-heart"></i><div><h3 id="inKindAssistanceDetails">{t.details}</h3><p>{t.detailsHint}</p></div></div>
              <div className="iap-profile-strip"><i className="fa-solid fa-id-card"></i><div><strong>{data.profile?.full_name}</strong><span>BEN-{data.profile?.beneficiary_id} · {data.profile?.file_status}</span></div><small>{[data.profile?.address, data.profile?.area].filter(Boolean).join(" · ")}</small></div>
              <div className="iap-form-grid">
                <label className="iap-field iap-field-wide"><span>{t.category}</span><select value={categoryId} onChange={(event) => { setCategoryId(event.target.value); setQuantities({}); setEligibility(null); }}><option value="">{t.select}</option>{data.categories.map((item) => <option value={item.id} key={item.id}>{isArabic ? item.category_name_ar || item.category_name : item.category_name} — {item.available_quantity} {t.available}</option>)}</select></label>
                {category ? <div className="iap-items iap-field-wide"><div className="iap-items-title"><h4>{t.items}</h4><span>{t.maximum} {category.maximum_quantity} · {t.waiting} {category.waiting_period_months} {t.months}</span></div><div className="iap-item-grid">{category.items.map((item) => <label className="iap-field" key={item.id}><span>{isArabic ? item.item_name_ar || item.item_name : item.item_name} ({item.unit})</span><input type="number" min="0" max={item.maximum_quantity || category.maximum_quantity} value={quantities[item.id] || ""} onChange={(event) => { setQuantities((current) => ({ ...current, [item.id]: event.target.value })); setEligibility(null); }} /></label>)}</div></div> : null}
                <label className="iap-field iap-field-wide"><span>{t.reason}</span><textarea value={reason} placeholder={t.reasonPlaceholder} onChange={(event) => setReason(event.target.value)} /></label>
                <label className="iap-field"><span>{t.method}</span><select value={method} onChange={(event) => setMethod(event.target.value)}><option value="HOME_DELIVERY">{t.home}</option><option value="TARAHUM_COLLECTION">{t.collection}</option></select></label>
                <label className="iap-field iap-field-notes"><span>{t.notes}</span><textarea value={notes} placeholder={t.notesPlaceholder} onChange={(event) => setNotes(event.target.value)} /></label>
                <label className="iap-field iap-field-wide iap-document-field"><span>{t.emiratesIdDocument}</span><input key={fileInputKey} type="file" accept="application/pdf,.pdf,image/jpeg,.jpg,.jpeg,image/png,.png" onChange={selectEmiratesIdDocument} required /><small>{emiratesIdDocument?.name || t.emiratesIdHint}</small></label>
              </div>
              {eligibility ? <div className={`iap-eligibility ${eligibility.eligible ? "ok" : "blocked"}`}><i className={`fa-solid ${eligibility.eligible ? "fa-circle-check" : "fa-circle-exclamation"}`}></i><div><strong>{eligibility.eligible ? t.eligible : t.notEligible}</strong><p>{eligibility.reason}</p></div></div> : null}
              <div className="iap-actions"><button type="button" className="iap-secondary" onClick={checkEligibility}><i className="fa-solid fa-shield-halved"></i>{t.check}</button><button type="button" className="iap-primary" disabled={!eligibility?.eligible || !reason.trim() || !emiratesIdDocument || saving} onClick={submit}><i className="fa-solid fa-paper-plane"></i>{saving ? t.submitting : t.submit}</button></div>
            </section> : null}
            <div className="iap-records-grid">
              <section className="iap-record-section"><div className="iap-record-heading"><i className="fa-regular fa-file-lines"></i><h3>{t.requests}</h3></div>{data.requests.length ? data.requests.map((request) => <article className="iap-row" key={request.id}><div><strong>{request.request_number}</strong><span>{request.category_name}</span></div><div><b>{String(request.status_code).replaceAll("_", " ")}</b><span>{formatDate(request.created_at)}</span></div></article>) : <div className="iap-empty"><i className="fa-regular fa-folder-open"></i><p>{t.noRequests}</p></div>}</section>
              <section className="iap-record-section"><div className="iap-record-heading"><i className="fa-solid fa-clock-rotate-left"></i><h3>{t.history}</h3></div>{data.history.length ? data.history.map((record) => <article className="iap-row" key={record.id}><div><strong>{record.category_name}</strong><span>{formatDate(record.distribution_date)} · {record.fulfillment_method}</span></div><div><b>{t.nextEligibility}</b><span>{formatDate(record.next_eligible_date)}</span></div></article>) : <div className="iap-empty"><i className="fa-regular fa-folder-open"></i><p>{t.noHistory}</p></div>}</section>
            </div>
          </>}
        </div></div></div>
      </div></div></section>
    </main>
    {isArabic ? <ArabicFooter /> : <Footer />}
  </>;
}
